import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { useBetaStore } from '@/stores/beta'
import { IndexedDBCaptureStorageV2, captureScopeKeyV2 } from '@/offline/capture/storageV2'
import type { CaptureScopeV2 } from '@/offline/capture/typesV2'
import { readCaptureIdentity, preparedCaptureWindowIsOpen } from '@/offline/capture/identity'
import { offlineCaptureAPI } from '@/api/offlineCapture'
import { OfflineCaptureCoordinator, emptyCoordinatorState, type CaptureSession } from './coordinator'
import { rememberLastPreparedCapture } from './resumePointer'

export const PREPARATIONS_CHANGED = 'coachsight-preparations-changed'
export interface PreparedSessionSummary {
  scope: CaptureScopeV2; title: string; preparedAt: string; endsAt: string; uploadUntil: string
  pending: number; attention: number; blocked: boolean; normalCaptureReady: boolean
}
export const usePreparedSessions = defineStore('prepared-sessions', () => {
  const beta = useBetaStore()
  const auth = useAuthStore(), storage = new IndexedDBCaptureStorageV2()
  const apiOrigin = new URL(import.meta.env.VITE_CSAPI_URL || location.origin, location.origin).origin
  const sessions = ref<PreparedSessionSummary[]>([]), loading = ref(false), syncing = ref(false), preparing = ref(''), error = ref('')
  const now = ref(Date.now())
  const pending = computed(() => sessions.value.reduce((sum, row) => sum + row.pending, 0))
  let generation = 0, inventoryGeneration = 0, running = false, stopped = true
  let timer: ReturnType<typeof setInterval> | undefined, retry: ReturnType<typeof setTimeout> | undefined, channel: BroadcastChannel | undefined
  const workers = new Set<OfflineCaptureCoordinator>()
  const backoff = new Map<string, { until: number; attempts: number }>()
  function account() {
    if (!beta.enabled) return null
    try {
      const token = localStorage.getItem('token') || ''
      const user = JSON.parse(localStorage.getItem('user') || 'null')
      if (token !== auth.token || user?.id !== auth.user?.id || localStorage.getItem('masqueradeOriginalUser')) return null
      const identity = readCaptureIdentity({ token, userId: auth.user?.id, isMasquerading: auth.isMasquerading })
      return identity ? { ...identity, token } : null
    } catch { return null }
  }
  function scopeFor(clubId: string, seasonId: string, eventId: string, sessionId: string): CaptureScopeV2 | null {
    const identity = account()
    if (!identity) return null
    const scope = { apiOrigin, realActorId: identity.actorId, effectiveActorId: identity.actorId, clubId, seasonId, eventId, sessionId }
    try { captureScopeKeyV2(scope); return scope } catch { return null }
  }
  function blocked(scope: CaptureScopeV2, value?: boolean) {
    const key = `capture-access-blocked:${JSON.stringify(scope)}`
    if (value === true) localStorage.setItem(key, '1')
    if (value === false) localStorage.removeItem(key)
    return localStorage.getItem(key) === '1'
  }
  async function canRestoreCapture(clubId: string, seasonId: string, eventId: string, sessionId: string): Promise<boolean> {
    try {
      const identity = account(), scope = scopeFor(clubId, seasonId, eventId, sessionId)
      if (!identity || !scope || blocked(scope)) return false
      const snapshot = await storage.getActiveSnapshot(scope)
      // Storage validates snapshot integrity; recheck identity after the asynchronous read.
      return account()?.token === identity.token && !blocked(scope) && !!snapshot &&
        snapshot.scorePayloadVersion === 2 && snapshot.actorId === identity.actorId &&
        snapshot.eventId === eventId && snapshot.sessionId === sessionId &&
        preparedCaptureWindowIsOpen(snapshot.preparedAt, snapshot.sessionEndsAt, Date.now())
    } catch { return false }
  }
  function isOpen(row: PreparedSessionSummary) { return !row.blocked && preparedCaptureWindowIsOpen(row.preparedAt, row.endsAt, now.value) }
  function changed() {
    channel?.postMessage({ type: 'changed', actorId: account()?.actorId })
    window.dispatchEvent(new Event(PREPARATIONS_CHANGED))
  }
  async function refresh() {
    const request = ++inventoryGeneration, epoch = generation, identity = account()
    if (!identity) { sessions.value = []; loading.value = false; return }
    const valid = () => request === inventoryGeneration && epoch === generation && account()?.token === identity.token
    loading.value = true
    try {
      const scopes = await storage.listPreparedScopes(apiOrigin, identity.actorId)
      const summaries: PreparedSessionSummary[] = []
      for (const scope of scopes) {
        if (!valid()) return
        // Read and release one bounded snapshot at a time; only summaries remain in memory.
        const snapshot = await storage.getActiveSnapshot(scope)
        if (!snapshot) continue
        const entries = await storage.list(scope)
        if (!valid()) return
        const locked = blocked(scope)
        summaries.push({ scope, title: !locked && preparedCaptureWindowIsOpen(snapshot.preparedAt, snapshot.sessionEndsAt, Date.now()) ? snapshot.resources.session.title : '',
          preparedAt: snapshot.preparedAt, endsAt: snapshot.sessionEndsAt, uploadUntil: snapshot.uploadUntil,
          pending: entries.filter(row => row.status === 'pending').length, attention: entries.filter(row => row.status === 'attention').length,
          blocked: locked, normalCaptureReady: snapshot.scorePayloadVersion === 2 })
      }
      if (valid()) { sessions.value = summaries; error.value = ''; now.value = Date.now() }
    } catch (cause) { if (valid()) error.value = (cause as {code?: string}).code || 'OFFLINE_STORAGE_FAILED' }
    finally { if (valid()) loading.value = false }
  }
  function worker(input: CaptureScopeV2) {
    // Summary rows are reactive. IDB accepts plain structured-cloneable values.
    const scope: CaptureScopeV2 = { ...input }
    const identity = account(), epoch = generation
    if (!identity || scope.apiOrigin !== apiOrigin || scope.realActorId !== identity.actorId || scope.effectiveActorId !== identity.actorId) throw new Error('OFFLINE_ACCOUNT_CHANGED')
    const current = (): CaptureSession | null => epoch === generation && account()?.token === identity.token
      ? { scope, token: identity.token, tokenExpiresAt: identity.tokenExpiresAt } : null
    const coordinator = new OfflineCaptureCoordinator(emptyCoordinatorState(), { storage, current, blocked,
      transport: { loadSnapshot: (s, signal) => offlineCaptureAPI.loadSnapshot(s.scope, s.scope.sessionId, s.token, signal), sync: (s, batch, signal) => offlineCaptureAPI.sync(s.scope, batch, s.token, signal) },
      lock: async (name, run) => await navigator.locks.request(name, { ifAvailable: true }, async lock => lock ? await run() : undefined), onChanged: changed })
    workers.add(coordinator)
    return coordinator
  }
  async function prepare(scope: CaptureScopeV2) {
    if (preparing.value || !navigator.onLine || !navigator.locks || !crypto.randomUUID) return false
    const epoch = generation, token = account()?.token
    let coordinator: OfflineCaptureCoordinator | undefined
    preparing.value = captureScopeKeyV2(scope)
    try {
      coordinator = worker(scope)
      await coordinator.restore()
      if (coordinator.state.error) throw Object.assign(new Error(), {code:coordinator.state.error})
      const snapshot = coordinator.state.snapshot
      // Never replace a preparation beneath pending work or conflict review.
      if (coordinator.state.entries.some(row => row.status === 'pending' || row.status === 'attention')) return !!snapshot && snapshot.scorePayloadVersion === 2 && !coordinator.state.serverLocked && preparedCaptureWindowIsOpen(snapshot.preparedAt, snapshot.sessionEndsAt, Date.now())
      if (snapshot?.scorePayloadVersion === 2 && !coordinator.state.serverLocked && preparedCaptureWindowIsOpen(snapshot.preparedAt, snapshot.sessionEndsAt, Date.now())) return true
      await coordinator.prepare()
      if (epoch !== generation || account()?.token !== token || !coordinator.state.snapshot) return false
      rememberLastPreparedCapture(scope, coordinator.state.snapshot.snapshotId)
      return true
    } catch (cause) { if (epoch === generation) error.value = (cause as {code?:string}).code || 'OFFLINE_STORAGE_FAILED'; return false }
    finally {
      if (coordinator) { coordinator.clear(); workers.delete(coordinator) }
      if (epoch === generation) { preparing.value = ''; await refresh(); changed() }
    }
  }
  async function syncAll(force = false) {
    if (running || stopped || !navigator.onLine || !navigator.locks || (account()?.tokenExpiresAt || 0) <= Date.now()) return
    const epoch = generation
    running = true; syncing.value = true
    try {
      await refresh()
      for (const row of sessions.value) {
        if (epoch !== generation || !account() || !navigator.onLine) break
        const key = captureScopeKeyV2(row.scope), last = backoff.get(key)
        if (!row.pending || Date.now() >= Date.parse(row.uploadUntil) || (!force && (row.blocked || (last && last.until > Date.now())))) continue
        let coordinator: OfflineCaptureCoordinator | undefined
        try {
          coordinator = worker(row.scope)
          await coordinator.sync()
          backoff.delete(key)
        } catch (cause) {
          const attempts = (last?.attempts || 0) + 1
          backoff.set(key, {attempts, until: (cause as {status?:number}).status === 422 ? Infinity : Date.now() + Math.min(60, 2 ** Math.min(attempts, 6)) * 1000})
        } finally { if (coordinator) { coordinator.clear(); workers.delete(coordinator) } }
      }
      if (epoch === generation) await refresh()
    } finally { running = false; if (epoch === generation) syncing.value = false }
  }
  function schedule() {
    if (stopped || retry) return
    retry = setTimeout(() => { retry = undefined; void syncAll() }, 1000)
  }
  function notified() { void refresh(); schedule() }
  function identityChanged() {
    generation++; inventoryGeneration++; sessions.value = []; loading.value = false; preparing.value = ''; syncing.value = false; error.value = ''; backoff.clear()
    workers.forEach(worker => worker.clear())
    void refresh(); schedule()
  }
  function storageChanged(event: StorageEvent) { if (!event.key || ['token','user','masqueradeOriginalUser'].includes(event.key)) identityChanged(); else if (event.key.startsWith('capture-access-blocked:')) notified() }
  let ticks = 0
  function start() {
    if (!beta.enabled || !stopped) return
    stopped = false
    channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('coachsight-capture-v2') : undefined
    if (channel) channel.onmessage = e => { if (e.data?.actorId === account()?.actorId) notified() }
    window.addEventListener(PREPARATIONS_CHANGED, notified); window.addEventListener('online', schedule); window.addEventListener('storage', storageChanged)
    timer = setInterval(() => {
      now.value = Date.now()
      for (const row of sessions.value) { if (!isOpen(row)) row.title = '' }
      if (++ticks % 10 === 0) schedule()
    }, 1000)
    identityChanged()
  }
  function stop() {
    stopped = true; generation++; inventoryGeneration++; workers.forEach(worker => worker.clear()); sessions.value = []
    clearInterval(timer); clearTimeout(retry); retry = undefined; channel?.close()
    window.removeEventListener(PREPARATIONS_CHANGED, notified); window.removeEventListener('online', schedule); window.removeEventListener('storage', storageChanged)
    storage.close()
  }
  watch(() => beta.enabled, enabled => { if (enabled) start(); else stop() }, {flush:'sync'})
  return {hasAccount: () => !!account(), sessions, loading, syncing, preparing, error, pending, now, scopeFor, canRestoreCapture, isOpen, refresh, prepare, syncAll, start, stop, identityChanged}
})
